"use client";

import { useLocale } from "@i18n/react";
import { useSearch } from "@packages/framework";
import { useQueryClient } from "@tanstack/react-query";

import type { AdminDashUser } from "../../../../lib/types/schema/community/members/adminDashMembersSchema";
import { adminDashUserSchema } from "../../../../lib/types/schema/community/members/adminDashMembersSchema";
import { useRequestMutation } from "../../../../request/hooks/useRequestMutation";
import type { UseRequestQueryProps } from "../../../../request/hooks/useRequestQuery";
import { useRequestQuery } from "../../../../request/hooks/useRequestQuery";
import { keyAdminDashUser } from "./keyAdminDashUser";
import { keyAdminDashUsers } from "./keyAdminDashUsers";

export type UseRequestAdminDashboardMemberProps = UseRequestQueryProps & {
  communityId: number;
  userId: number;
};

export const useRequestAdminDashboardMember = ({
  communityId,
  userId,
  ...props
}: UseRequestAdminDashboardMemberProps) => {
  const queryClient = useQueryClient();
  const search = useSearch();
  const locale = useLocale();

  return {
    query: useRequestQuery({
      ...props,
      queryFnRequestProps: () => ({
        schema: adminDashUserSchema,
        urlBackendPath: `/communities/${communityId}/admin/users/${userId}`,
      }),
      queryKey: keyAdminDashUser({ communityId, userId }),
    }),
    mutationPut: useRequestMutation<
      typeof adminDashUserSchema,
      Partial<AdminDashUser>
    >({
      mutationFnRequestProps: (values) => ({
        method: "PUT",
        urlBackendPath: `/communities/${communityId}/admin/users/${userId}`,
        body: JSON.stringify(values),
      }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: keyAdminDashUser({ communityId, userId }),
        });
        queryClient.invalidateQueries({
          queryKey: [
            keyAdminDashUsers({
              id: communityId,
              search: search.get("search"),
              filter:
                search.get("filter") === "students" ? "student" : "teacher",
            }),
            locale,
          ],
        });
      },
    }),
    mutationDelete: useRequestMutation({
      mutationFnRequestProps: {
        urlBackendPath: `/communities/${communityId}/admin/users/${userId}`,
        method: "DELETE",
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: keyAdminDashUser({ communityId, userId }),
        });
        queryClient.invalidateQueries({
          queryKey: [
            keyAdminDashUsers({
              id: communityId,
              search: search.get("search"),
              filter:
                search.get("filter") === "students" ? "student" : "teacher",
            }),
            locale,
          ],
        });
      },
    }),
  };
};
